/**
 * Dynamic Field Component
 * 
 * WHY: The Form Factory pattern requires rendering fields dynamically
 * based on schema definitions. This component maps schema field types
 * to the appropriate Shadcn UI components.
 * 
 * PHASE 5 UPDATE: Refactored from inline HTML to use proper Shadcn primitives
 * for consistent styling and accessibility.
 * 
 * @see .docs/architecture/software_iq_dashboard.md - Section 2.2
 */

import { Control, Controller, FieldValues, Path } from 'react-hook-form'
import type { SchemaField } from '@/api/core/types'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface DynamicFieldProps<T extends FieldValues> {
  field: SchemaField
  control: Control<T>
  /** Whether the field should be hidden based on visible_if condition */
  isHidden?: boolean
  /** Whether the field is conditionally required */
  isConditionallyRequired?: boolean
}

/**
 * Renders a form field based on schema definition.
 * Maps field_type and ui_hints to appropriate Shadcn components.
 */
export function DynamicField<T extends FieldValues>({
  field,
  control,
  isHidden = false,
  isConditionallyRequired = false,
}: DynamicFieldProps<T>) {
  if (isHidden) {
    return null
  }

  const isRequired = field.required || isConditionallyRequired
  const inputType = field.ui_hints?.input_type || getDefaultInputType(field.field_type)

  return (
    <div className="space-y-2">
      <Label htmlFor={field.field_id}>
        {field.display_name}
        {isRequired && <span className="text-destructive ml-1">*</span>}
      </Label>

      <Controller
        name={field.field_id as Path<T>}
        control={control}
        render={({ field: formField, fieldState }) => (
          <div className="space-y-1">
            <FieldInput
              schemaField={field}
              formField={formField}
              inputType={inputType}
            />
            {field.ui_hints?.help_text && (
              <p className="text-xs text-muted-foreground">{field.ui_hints.help_text}</p>
            )}
            {fieldState.error && (
              <p className="text-xs text-destructive">{fieldState.error.message}</p>
            )}
          </div>
        )}
      />
    </div>
  )
}

/**
 * Determines the default input type based on field type.
 */
function getDefaultInputType(fieldType: string): string {
  switch (fieldType) {
    case 'number':
      return 'number'
    case 'boolean':
      return 'checkbox'
    case 'enum':
      return 'select'
    case 'date':
      return 'datepicker'
    default:
      return 'text'
  }
}

/**
 * Props for the internal FieldInput component.
 */
interface FieldInputProps {
  schemaField: SchemaField
  formField: {
    onChange: (value: unknown) => void
    onBlur: () => void
    value: unknown
    name: string
    ref: React.Ref<HTMLElement>
  }
  inputType: string
}

/**
 * Renders the appropriate Shadcn input component based on input type.
 */
function FieldInput({ schemaField, formField, inputType }: FieldInputProps) {
  switch (inputType) {
    case 'textarea':
      return (
        <Textarea
          id={schemaField.field_id}
          name={formField.name}
          ref={formField.ref as React.Ref<HTMLTextAreaElement>}
          value={(formField.value as string) || ''}
          onChange={(e) => formField.onChange(e.target.value)}
          onBlur={formField.onBlur}
          placeholder={schemaField.ui_hints?.placeholder}
          className="min-h-[80px] resize-y"
        />
      )

    case 'number':
      return (
        <Input
          id={schemaField.field_id}
          type="number"
          name={formField.name}
          ref={formField.ref as React.Ref<HTMLInputElement>}
          value={(formField.value as number) ?? ''}
          onChange={(e) => formField.onChange(e.target.valueAsNumber || undefined)}
          onBlur={formField.onBlur}
          placeholder={schemaField.ui_hints?.placeholder}
          step={schemaField.ui_hints?.step}
          min={schemaField.validation?.minimum}
          max={schemaField.validation?.maximum}
        />
      )

    case 'checkbox':
      return (
        <div className="flex items-center space-x-2">
          <Checkbox
            id={schemaField.field_id}
            checked={!!formField.value}
            onCheckedChange={(checked) => formField.onChange(checked)}
            onBlur={formField.onBlur}
          />
          {schemaField.description && (
            <Label htmlFor={schemaField.field_id} className="text-sm font-normal text-muted-foreground">
              {schemaField.description}
            </Label>
          )}
        </div>
      )

    case 'select':
      return (
        <Select
          value={(formField.value as string) || ''}
          onValueChange={(value) => formField.onChange(value)}
        >
          <SelectTrigger id={schemaField.field_id} onBlur={formField.onBlur}>
            <SelectValue
              placeholder={schemaField.ui_hints?.placeholder || `Select ${schemaField.display_name}`}
            />
          </SelectTrigger>
          <SelectContent>
            {/* Render options from ui_hints if available */}
            {schemaField.ui_hints?.options?.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
            {/* Fallback to enum values if no options defined */}
            {!schemaField.ui_hints?.options &&
              schemaField.validation?.enum?.map((value) => (
                <SelectItem key={value} value={value}>
                  {value}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      )

    case 'text':
    default:
      return (
        <Input
          id={schemaField.field_id}
          type="text"
          name={formField.name}
          ref={formField.ref as React.Ref<HTMLInputElement>}
          value={(formField.value as string) || ''}
          onChange={(e) => formField.onChange(e.target.value)}
          onBlur={formField.onBlur}
          placeholder={schemaField.ui_hints?.placeholder}
          maxLength={schemaField.validation?.max_length}
        />
      )
  }
}
